// Central content data for CareDeserved marketing site.

export const COMPANY = {
  name: "CareDeserved LLC",
  short: "CareDeserved",
  tagline: "Care Beyond the Ride.",
  phone: "484-877-5855",
  phoneHref: "tel:+14848775855",
  email: "info@caredeserved.com",
  careersEmail: "careers@caredeserved.com",
  website: "CareDeserved.com",
};

export const SERVICES = [
  {
    slug: "nemt",
    path: "/services/nemt",
    icon: "Ambulance",
    title: "Non-Emergency Medical Transportation",
    short: "Non-Emergency Medical Transportation",
    card: "Safe and dependable transportation to medical appointments, hospitals, treatment centers, dialysis, therapy, and other healthcare destinations.",
    cta: "Learn More",
    hero: "Safe, Dependable Non-Emergency Medical Transportation",
    intro: "CareDeserved provides safe and dependable non-emergency medical transportation for passengers traveling to and from healthcare destinations across Pennsylvania. Whether it is a routine check-up or ongoing treatment, our team is committed to getting passengers there safely, comfortably, and on time.",
    points: [
      "Medical appointment transportation",
      "Dialysis and treatment center transportation",
      "Hospital and facility transportation",
      "Therapy and follow-up visits",
      "Discharge and return transportation",
    ],
    body: "We understand how important it is to reach medical care reliably. Our professional drivers are trained to assist passengers with care and respect, ensuring a smooth experience from pickup to drop-off. We coordinate with brokers, facilities, and families to make transportation simple and stress-free.",
  },
  {
    slug: "wheelchair-transportation",
    path: "/services/wheelchair-transportation",
    icon: "Wheelchair",
    title: "Wheelchair Accessible Transportation",
    short: "Wheelchair Transportation",
    card: "Accessible transportation with trained drivers and properly equipped vehicles designed for passenger comfort, dignity, and safety.",
    cta: "Learn More",
    hero: "Accessible Transportation Built Around Comfort & Dignity",
    intro: "Our wheelchair accessible transportation is designed for passengers who require additional support. With properly equipped vehicles and trained drivers, we prioritize comfort, dignity, and safety on every trip.",
    points: [
      "ADA-minded, properly equipped vehicles",
      "Trained drivers who assist with boarding",
      "Secure wheelchair loading and fastening",
      "Door-to-door assistance",
      "Respectful, patient service",
    ],
    body: "Every passenger deserves to feel safe and respected. Our drivers are trained in safe wheelchair securement and compassionate assistance, so passengers and their families can travel with peace of mind.",
  },
  {
    slug: "ambulatory-transportation",
    path: "/services/ambulatory-transportation",
    icon: "PersonSimpleWalk",
    title: "Ambulatory Transportation",
    short: "Ambulatory Transportation",
    card: "Reliable transportation for passengers who are able to enter and exit vehicles independently or with minimal assistance.",
    cta: "Learn More",
    hero: "Reliable Rides for Ambulatory Passengers",
    intro: "Ambulatory transportation is ideal for passengers who can enter and exit vehicles independently or with minimal assistance. We provide dependable, comfortable rides for appointments and everyday destinations.",
    points: [
      "Comfortable sedan and van transportation",
      "Minimal assistance available",
      "On-time, reliable scheduling",
      "Friendly, professional drivers",
      "One-way and round trip options",
    ],
    body: "For passengers who need reliable transportation without specialized equipment, our ambulatory service delivers a smooth, courteous experience every time.",
  },
  {
    slug: "odp-transportation",
    path: "/services/odp-transportation",
    icon: "UsersThree",
    title: "ODP & Community Transportation",
    short: "ODP & Community Transportation",
    card: "Transportation for individuals participating in ODP services, day programs, employment, community activities, and other approved destinations.",
    cta: "Learn More",
    hero: "Transportation That Supports Independence and Community Participation",
    intro: "CareDeserved can support transportation related to day programs, employment, community participation, activities, approved appointments, and program transportation. We use respectful, person-centered service on every trip.",
    points: [
      "Day programs",
      "Employment",
      "Community participation",
      "Activities",
      "Approved appointments",
      "Program transportation",
    ],
    body: "For individuals, families, supports coordinators, agencies, and providers interested in transportation services, contact CareDeserved to discuss availability and service needs.",
  },
  {
    slug: "private-pay",
    path: "/private-pay",
    icon: "CreditCard",
    title: "Private Pay Transportation",
    short: "Private Pay Transportation",
    card: "Convenient transportation options for individuals, families, and caregivers who need reliable transportation outside of broker-arranged trips.",
    cta: "Request a Quote",
  },
  {
    slug: "facility-transportation-solutions",
    path: "/facilities",
    icon: "Buildings",
    title: "Facility Transportation Solutions",
    short: "Facility Transportation Solutions",
    card: "Reliable transportation solutions for healthcare facilities, agencies, programs, case managers, and community organizations.",
    cta: "Partner With Us",
  },
];

export const SERVICE_DETAIL = Object.fromEntries(
  SERVICES.filter((s) => s.points).map((s) => [s.slug, s])
);

export const VALUES = [
  { icon: "ShieldCheck", title: "Safety First", text: "Your safety is our priority on every trip." },
  { icon: "Clock", title: "On-Time Service", text: "We value your time and work hard to provide dependable transportation." },
  { icon: "HeartHalf", title: "Compassionate Team", text: "Our drivers are professional, respectful, and committed to passenger care." },
  { icon: "MapPinLine", title: "Community Focused", text: "We proudly serve individuals, families, healthcare facilities, and community organizations." },
];

export const COUNTIES = [
  "Delaware", "Chester", "Berks", "Bucks", "Northampton",
  "Lehigh", "Lebanon", "Lancaster", "York", "Schuylkill",
];

export const FACILITY_BENEFITS = [
  "Professional drivers",
  "Ambulatory and wheelchair transportation",
  "Reliable scheduling",
  "Responsive communication",
  "Multiple county coverage",
  "Facility transportation solutions",
  "Ongoing transportation relationships",
];

export const NAV = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  {
    label: "Services", to: "/services",
    children: [
      { label: "Non-Emergency Medical Transportation", to: "/services/nemt" },
      { label: "Wheelchair Transportation", to: "/services/wheelchair-transportation" },
      { label: "Ambulatory Transportation", to: "/services/ambulatory-transportation" },
      { label: "ODP & Community Transportation", to: "/services/odp-transportation" },
      { label: "Private Pay Transportation", to: "/private-pay" },
      { label: "Facility Transportation Solutions", to: "/facilities" },
    ],
  },
  { label: "ODP Services", to: "/odp-services" },
  { label: "For Clients", to: "/for-clients" },
  { label: "For Facilities & Partners", to: "/facilities" },
  { label: "Service Areas", to: "/service-areas" },
  { label: "Careers", to: "/careers" },
  { label: "Contact Us", to: "/contact" },
];

export const IMAGES = {
  hero: "https://static.prod-images.emergentagent.com/jobs/c740ef60-9796-414f-9d72-18301a696943/images/8e054e59c25b8852cb9cec924b6e33149fb2059ac9d5a3221eb83604641defee.jpeg",
  facilities: "https://images.unsplash.com/photo-1764885517847-79d62138cc58?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  care: "https://static.prod-images.emergentagent.com/jobs/c740ef60-9796-414f-9d72-18301a696943/images/96b4d6da34de8fb29ded6da767af7f5d869f7eb9a0e1039bedbc8d6911ba7211.jpeg",
  nemt: "https://static.prod-images.emergentagent.com/jobs/c740ef60-9796-414f-9d72-18301a696943/images/d03e8cb0933af39832e4e0c516a3d5e7f2cad846c794ef8b74f2d2534a05878e.jpeg",
  wheelchair: "https://static.prod-images.emergentagent.com/jobs/c740ef60-9796-414f-9d72-18301a696943/images/9c60cc0ec0a1d7b634b97121fd5196cebd1c12048e3efc000b37a91a1998c6e3.jpeg",
  ambulatory: "https://static.prod-images.emergentagent.com/jobs/c740ef60-9796-414f-9d72-18301a696943/images/de11af5a77de32b923e1348f56eea21bc7947ef7f1af9a7ea7b0f64fe325cf91.jpeg",
  odpHab: "https://static.prod-images.emergentagent.com/jobs/c740ef60-9796-414f-9d72-18301a696943/images/3a58f59be4966217f1a529f2966f5a37ef3c619a46ff4c087fd07d17e9ed951b.jpeg",
  odpCompanion: "https://static.prod-images.emergentagent.com/jobs/c740ef60-9796-414f-9d72-18301a696943/images/ca975d2c729a9991a5e45c60f5d4c9a639f236311baad42d25f0dfd3b2b0933d.jpeg",
  odpNursing: "https://static.prod-images.emergentagent.com/jobs/c740ef60-9796-414f-9d72-18301a696943/images/96b4d6da34de8fb29ded6da767af7f5d869f7eb9a0e1039bedbc8d6911ba7211.jpeg",
  odpCommunity: "https://static.prod-images.emergentagent.com/jobs/c740ef60-9796-414f-9d72-18301a696943/images/a0a677e5512b402eb042f4efa058f90c4cfc22aaf50f4e28b5bab98d17d14b4a.jpeg",
  recruitment: "https://static.prod-images.emergentagent.com/jobs/c740ef60-9796-414f-9d72-18301a696943/images/30dc903db1e42b4c13eea02d36de3f2d2845c6f57aa2444965051cec4fe04b0e.jpeg",
};

export const ODP_SERVICE_OPTIONS = [
  "Home & Community Habilitation",
  "Companion Services",
  "Registered Nursing",
  "Non-Emergency Transportation",
];

export const ODP_SERVICES = [
  { icon: "HandHeart", title: "Home & Community Habilitation", text: "Person-centered support designed to help individuals develop, maintain, and strengthen skills for greater independence at home and in the community." },
  { icon: "Users", title: "Companion Services", text: "Support and companionship focused on community participation, social engagement, supervision, and helping individuals remain connected to everyday life." },
  { icon: "FirstAid", title: "Registered Nursing", text: "Qualified registered nursing services delivered according to the individual's authorized needs and applicable ODP requirements." },
  { icon: "Ambulance", title: "Non-Emergency Transportation", text: "Safe and dependable transportation supporting access to authorized programs, activities, appointments, employment, and community participation." },
];

// Unique, non-duplicated local content for each of the 10 service counties.
export const COUNTY_SEO = {
  "delaware-county-pa": {
    seat: "Media",
    towns: ["Chester", "Upper Darby", "Drexel Hill", "Lansdowne", "Springfield", "Ridley Park"],
    intro:
      "Just west of Philadelphia, Delaware County is one of Pennsylvania's most densely populated areas — and one where dependable medical transportation matters most. CareDeserved provides safe, on-time non-emergency medical transportation for residents from Media and Springfield to Upper Darby, Chester, and beyond.",
    destinations:
      "We regularly transport passengers to hospitals and outpatient centers throughout the county, including dialysis clinics, cardiology and oncology appointments, physical therapy, and routine follow-up visits.",
    note: "With heavy traffic along the I-95 and Route 1 corridors, our drivers plan ahead so Delaware County passengers still arrive relaxed and on schedule.",
  },
  "chester-county-pa": {
    seat: "West Chester",
    towns: ["Coatesville", "Downingtown", "Kennett Square", "Phoenixville", "Exton", "Oxford"],
    intro:
      "From the borough of West Chester to the rural stretches around Oxford and Kennett Square, Chester County blends busy suburbs with wide open farmland. CareDeserved bridges that distance with reliable wheelchair and ambulatory transportation for every kind of medical trip.",
    destinations:
      "Common destinations include county hospitals and health campuses, dialysis and infusion centers, senior living communities, and specialist offices in Exton, Downingtown, and Phoenixville.",
    note: "For passengers in Chester County's more rural townships, we specialize in longer-distance rides that keep riders comfortable the whole way.",
  },
  "berks-county-pa": {
    seat: "Reading",
    towns: ["Wyomissing", "Sinking Spring", "Kutztown", "Boyertown", "Muhlenberg", "Hamburg"],
    intro:
      "Centered on the city of Reading, Berks County is home to a large and growing population of older adults and individuals who depend on scheduled medical care. CareDeserved is proud to serve Berks with compassionate, professional transportation every day of the week.",
    destinations:
      "We take passengers to major Reading-area hospitals and health systems, dialysis centers, treatment and rehabilitation facilities, and day programs across Wyomissing, Muhlenberg, and Kutztown.",
    note: "Reading's dialysis and treatment schedules run early — our team is on the road before dawn so no Berks County passenger misses a session.",
  },
  "bucks-county-pa": {
    seat: "Doylestown",
    towns: ["Levittown", "Bensalem", "Newtown", "Quakertown", "Warminster", "Perkasie"],
    intro:
      "Stretching from Levittown and Bensalem in the south to Quakertown and the Upper Bucks countryside, Bucks County covers a lot of ground. CareDeserved provides seamless non-emergency medical transportation across all of it, tailored to each passenger's needs.",
    destinations:
      "Riders travel with us to Doylestown-area hospitals, outpatient surgical and imaging centers, dialysis clinics, and senior communities throughout Newtown, Warminster, and Perkasie.",
    note: "Whether it's a quick trip in Lower Bucks or a longer ride from the northern townships, Bucks County passengers get the same courteous, door-to-door service.",
  },
  "northampton-county-pa": {
    seat: "Easton",
    towns: ["Bethlehem", "Nazareth", "Bangor", "Wilson", "Hellertown", "Pen Argyl"],
    intro:
      "Anchored by Easton and part of the Lehigh Valley, Northampton County combines vibrant city neighborhoods with quiet boroughs like Nazareth and Bangor. CareDeserved keeps residents connected to the care they need with safe, accessible rides.",
    destinations:
      "Our passengers frequently visit Lehigh Valley and St. Luke's network facilities, dialysis and cancer treatment centers, and rehabilitation and therapy providers around Bethlehem and Wilson.",
    note: "As part of the greater Lehigh Valley, Northampton County riders benefit from our deep familiarity with the region's major medical campuses.",
  },
  "lehigh-county-pa": {
    seat: "Allentown",
    towns: ["Bethlehem", "Emmaus", "Whitehall", "Macungie", "Coopersburg", "Slatington"],
    intro:
      "Lehigh County and its hub city of Allentown form the heart of the Lehigh Valley. With one of the region's largest concentrations of hospitals and specialty clinics, dependable medical transportation is essential — and it's exactly what CareDeserved delivers.",
    destinations:
      "We transport passengers to Lehigh Valley Health Network and St. Luke's campuses, dialysis and infusion centers, day programs, and outpatient offices in Emmaus, Whitehall, and Macungie.",
    note: "From downtown Allentown to the western townships, our drivers know the fastest, calmest routes to every Lehigh County health destination.",
  },
  "lebanon-county-pa": {
    seat: "Lebanon",
    towns: ["Palmyra", "Annville", "Myerstown", "Cornwall", "Jonestown", "Cleona"],
    intro:
      "Smaller and more close-knit, Lebanon County still has real transportation needs — especially for veterans, seniors, and individuals in day programs. CareDeserved provides personal, reliable rides throughout Lebanon and its surrounding boroughs.",
    destinations:
      "Passengers ride with us to Lebanon's hospitals and the VA Medical Center, dialysis and rehabilitation centers, and appointments in Palmyra, Annville, and Myerstown.",
    note: "In a community where neighbors know neighbors, our drivers become a trusted, familiar part of each Lebanon County passenger's routine.",
  },
  "lancaster-county-pa": {
    seat: "Lancaster",
    towns: ["Ephrata", "Lititz", "Elizabethtown", "Columbia", "Manheim", "Strasburg"],
    intro:
      "Lancaster County pairs a busy city with some of Pennsylvania's most rural farm country. CareDeserved serves both — providing wheelchair-accessible and ambulatory transportation from the city of Lancaster out to Ephrata, Lititz, and Elizabethtown.",
    destinations:
      "Our riders travel to Lancaster's major hospitals and health campuses, dialysis and treatment centers, senior living communities, and specialist visits throughout Columbia and Manheim.",
    note: "For passengers in Lancaster County's outlying townships, we plan generous travel time so every rider arrives unhurried and cared for.",
  },
  "york-county-pa": {
    seat: "York",
    towns: ["Hanover", "Red Lion", "Dover", "Dallastown", "Spring Grove", "Shrewsbury"],
    intro:
      "From the city of York down to the Maryland line, York County covers a wide mix of urban, suburban, and rural communities. CareDeserved provides steady, compassionate medical transportation across all of them.",
    destinations:
      "Passengers count on us for trips to York-area hospitals and health networks, dialysis and cancer treatment centers, therapy clinics, and appointments in Hanover, Red Lion, and Dallastown.",
    note: "With commuters filling the I-83 corridor each morning, our drivers build in extra time so York County riders never feel rushed.",
  },
  "schuylkill-county-pa": {
    seat: "Pottsville",
    towns: ["Tamaqua", "Shenandoah", "Frackville", "Mahanoy City", "Schuylkill Haven", "Minersville"],
    intro:
      "In the mountains of Pennsylvania's coal region, Schuylkill County's towns are often a distance from major medical centers. CareDeserved closes that gap with reliable long-distance non-emergency medical transportation for every Schuylkill County resident.",
    destinations:
      "We regularly drive passengers from Pottsville, Tamaqua, and Frackville to regional hospitals, dialysis clinics, and specialty and treatment centers both in-county and in the neighboring Lehigh Valley.",
    note: "Longer trips are our specialty here — Schuylkill County passengers ride in comfort even when care is an hour or more away.",
  },
};

// City -> county lookup for approximate IP-based recruitment targeting (built from COUNTY_SEO).
export const CITY_TO_COUNTY = (() => {
  const map = {};
  Object.entries(COUNTY_SEO).forEach(([slug, v]) => {
    const county = COUNTIES.find((c) => slug === `${c.toLowerCase()}-county-pa`);
    if (!county) return;
    [county, v.seat, ...(v.towns || [])].filter(Boolean).forEach((place) => {
      map[place.toLowerCase()] = county;
    });
  });
  return map;
})();
